import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import cleanDeep from 'clean-deep';
import { parsePhoneNumber } from 'libphonenumber-js';
import isEmpty from 'lodash-es/isEmpty';
import isPlainObject from 'lodash-es/isPlainObject';
import merge from 'lodash-es/merge';
import mergeWith from 'lodash-es/mergeWith';
import { Subject } from 'rxjs';
import { phoneNumberAndCountryCodeValidator } from 'src/app/shared/shared-validators/phone-number-validator';
import { countryCodes } from 'src/assets/country-code';
import { ConsentSource, CreatePatientInput, Patient, UpdatePatientInput } from '../../../API';
import { HealthCardMode, LocationHealthCardModeMap } from '../location-select/location.model';
import { emailValidator } from '../shared-validators/email-validator';
import { phoneNumberValidator } from '../shared-validators/phone-number-validator';
import { isNullOrUndefined } from '../util/helper.util';

export enum PatientEntryMethod {
  AddNewPatient = 'addNewPatient',
  LookupExisting = 'lookupExisting'
}

export enum PatientFormControlType {
  // The enum values can be set to anything, but for now they are the names of the form control themselves
  Id = 'id',
  PatientDoctorId = 'patientDoctorId',
  EntryMethod = 'entryMethod',
  FirstName = 'firstName',
  LastName = 'lastName',
  DateOfBirth = 'dateOfBirth',
  HealthCard = 'healthCard',
  Gender = 'gender',
  GenderOther = 'genderOther',
  Phone = 'phone',
  Email = 'email',
  Address = 'address',
  ReferralSource = 'referred by',
  ConsentsToResearch = 'consentsToResearch',
  ConsentSource = 'consentSource',
  consentsToPrivacyForm = 'consentsToPrivacyForm',
  Other = 'other',
  LinkedReferralPatientId = 'linkedReferralPatientId',
  City = 'city',
  Province = 'province',
  Country = 'country',
  PostalCode = 'postalCode',
  EmergencyContactName = 'emergencyContactName',
  EmergencyContactEmail = 'emergencyContactEmail',
  PhoneNumberAndCountryCode = 'phoneNumberAndCountryCode',
  ExternalPatientId = 'externalPatientId',
  EmergencyContactPhoneNumberAndCountryCode = 'emergencyContactPhoneNumberAndCountryCode',
  pharmacyName = 'pharmacyName',
  pharmacyPhoneNumberAndCountryCode = 'pharmacyPhoneNumberAndCountryCode',
  pharmacyFaxNumberAndCountryCode = 'pharmacyFaxNumberAndCountryCode'
}

interface HealthCardFormControls {
  province: FormControl;
  number: FormControl;
  country: FormControl;
}

export class HealthCardFormGroup extends FormGroup {
  constructor() {
    super(
      {
        province: new FormControl(),
        number: new FormControl(''),
        country: new FormControl()
      },
      [HealthCardFormGroup.healthCardValidator]
    );
  }
  controls: HealthCardFormControls & { [key: string]: AbstractControl };

  private static healthCardValidator = (control: HealthCardFormGroup) => {
    if (control) {
      let isValid = true;
      let errorMessage: string;

      const healthCardConfig: HealthCardMode =
        LocationHealthCardModeMap[control.controls.country.value];

      if (healthCardConfig === HealthCardMode.provinceAndNumber) {
        if (control.controls.number.value && !control.controls.province.value) {
          isValid = false;
          errorMessage = 'State/Prov must be selected if healthcare # is filled';
        }
        if (control.controls.province.value && !control.controls.number.value) {
          isValid = false;
          errorMessage = 'Healthcard # must be filled if state/prov is selected';
        }
      }

      return isValid ? null : { healthCardInvalid: errorMessage };
    }
    return null;
  };

  public reset(
    value?: any,
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
    }
  ) {
    // Add this since GraphQL can return null for a value
    // If we find we need this in many places, then maybe create a CSIFormGroup class that
    // extends FormGroup with this utility method
    if (value === null) {
      value = {};
    }

    super.reset(value, options);
  }

  patchValue(
    value: {
      [key: string]: any;
    },
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
    }
  ) {
    // See https://github.com/angular/angular/issues/21021
    if (value === null) {
      value = {};
    }
    if (value.province && value.number) {
      value.number = value.province + value.number;
      value.province = '';
    }

    super.patchValue(value, options);
  }
}

// Rebuilding the Date from its parts and comparing them back catches impossible dates like
// 1990-02-30, which new Date() would silently roll over to March 2nd. Shared by the dateOfBirth
// validator, the CSV date parser and toAWSDate so they can never disagree on what a real date is.
export function isRealCalendarDate(year: number, monthIndex: number, dayOfMonth: number): boolean {
  const rebuiltDate = new Date(year, monthIndex, dayOfMonth);
  return (
    rebuiltDate.getFullYear() === year &&
    rebuiltDate.getMonth() === monthIndex &&
    rebuiltDate.getDate() === dayOfMonth
  );
}

// Canonical AWSDate storage format (YYYY-MM-DD). Manual zero-padding — the project's TS 3.x lib
// target has no String.prototype.padStart.
export function formatDateAsAWSDateString(date: Date): string {
  const monthNumber = date.getMonth() + 1;
  const dayNumber = date.getDate();
  const zeroPaddedMonth = (monthNumber < 10 ? '0' : '') + monthNumber;
  const zeroPaddedDay = (dayNumber < 10 ? '0' : '') + dayNumber;
  return date.getFullYear() + '-' + zeroPaddedMonth + '-' + zeroPaddedDay;
}

// A non-empty string date of birth must be a real calendar date in a sane birth range: the
// YYYY-MM-DD storage format (legacy non-zero-padded values tolerated, optional ISO time suffix),
// year 1900 or later, and never in the future. Non-string values keep the pre-existing NaN
// check — the birthday picker propagates Date objects, which must stay valid. Empty stays valid
// because date of birth is optional.
const dateOfBirthValidator = (control: AbstractControl) => {
  const dateOfBirthValue = control.value;
  if (!dateOfBirthValue) {
    return null;
  }
  if (typeof dateOfBirthValue !== 'string') {
    return isNaN(dateOfBirthValue) ? { invalidDate: 'Date is invalid' } : null;
  }
  const dateParts = dateOfBirthValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(T|$)/);
  if (!dateParts) {
    return { invalidDate: 'Date is invalid' };
  }
  const year = Number(dateParts[1]);
  const monthIndex = Number(dateParts[2]) - 1;
  const dayOfMonth = Number(dateParts[3]);
  const parsedDateOfBirth = new Date(year, monthIndex, dayOfMonth);
  const isInSaneBirthRange = year >= 1900 && parsedDateOfBirth.getTime() <= new Date().getTime();
  return isRealCalendarDate(year, monthIndex, dayOfMonth) && isInSaneBirthRange
    ? null
    : { invalidDate: 'Date is invalid' };
};

interface PatientFormControls {
  id: FormControl & { member: PatientFormControlType };
  patientDoctorId: FormControl & { member: PatientFormControlType };
  entryMethod: FormControl & { member: PatientFormControlType };
  firstName: FormControl & { member: PatientFormControlType };
  lastName: FormControl & { member: PatientFormControlType };
  dateOfBirth: FormControl & { member: PatientFormControlType };
  healthCard: HealthCardFormGroup & { member: PatientFormControlType };
  gender: FormControl & { member: PatientFormControlType };
  genderOther: FormControl & { member: PatientFormControlType };
  phone: FormControl & { member: PatientFormControlType };
  email: FormControl & { member: PatientFormControlType };
  address: FormControl & { member: PatientFormControlType };
  city: FormControl & { member: PatientFormControlType };
  province: FormControl & { member: PatientFormControlType };
  country: FormControl & { member: PatientFormControlType };
  postalCode: FormControl & { member: PatientFormControlType };
  referralSource: FormControl & { member: PatientFormControlType };
  consentsToResearch: FormControl & { member: PatientFormControlType };
  consentSource: FormControl & { member: PatientFormControlType };
  consentsToPrivacyForm: FormControl & { member: PatientFormControlType };
  linkedReferralPatientId: FormControl & { member: PatientFormControlType };
  phoneNumberAndCountryCode: FormGroup & { member: PatientFormControlType };
  externalPatientId: FormControl & { member: PatientFormControlType };
  emergencyContactName: FormGroup & { member: PatientFormControlType };
  emergencyContactEmail: FormGroup & { member: PatientFormControlType };
  emergencyContactPhoneNumberAndCountryCode: FormGroup & { member: PatientFormControlType };
  pharmacyName: FormControl & { member: PatientFormControlType };
  pharmacyPhoneNumberAndCountryCode: FormGroup & { member: PatientFormControlType };
  pharmacyFaxNumberAndCountryCode: FormGroup & { member: PatientFormControlType };
}

export class PatientFormGroup extends FormGroup {
  controls: PatientFormControls & {
    [key: string]: AbstractControl & { member: PatientFormControlType };
  };
  // Original CSV data-row index (0-based, after header removal) when this group was built by the
  // patient import wizard; null everywhere else. Lets the flagged-row review report stable CSV
  // row numbers even after rows are removed from the import.
  public csvImportRowIndex: number = null;
  private _submitted = false;
  public submitted$ = new Subject<boolean>();
  set submitted(submitted: boolean) {
    if (submitted) {
      this._submitted = submitted;
      this.submitted$.next(this._submitted);
      this.markAllAsTouched();
    }
  }

  get submitted(): boolean {
    return this._submitted;
  }

  static getPhoneNumberAndCountryCodeFormGroup(required: boolean = false) {
    return new FormGroup(
      {
        phone: new FormControl(null, required ? [Validators.required] : []),
        countryCode: new FormControl()
      },
      {
        validators: phoneNumberAndCountryCodeValidator('phone', 'countryCode')
      }
    );
  }

  static syncPhoneNumberWithPhoneNumberAndCountryCodeFormGroup(
    phoneNumberAndCountryCode,
    phoneControl
  ) {
    phoneNumberAndCountryCode.valueChanges.subscribe(({ phone, countryCode }) => {
      if (phone && countryCode) {
        phoneControl.setValue(countryCode + phone);
      }
    });
  }

  constructor() {
    super({
      id: new FormControl(),
      patientDoctorId: new FormControl(),
      entryMethod: new FormControl(PatientEntryMethod.AddNewPatient),
      firstName: new FormControl('', Validators.required),
      lastName: new FormControl('', Validators.required),
      dateOfBirth: new FormControl('', [dateOfBirthValidator]),
      healthCard: new HealthCardFormGroup(),
      gender: new FormControl(''),
      genderOther: new FormControl(''),
      phone: new FormControl('', phoneNumberValidator),
      email: new FormControl('', emailValidator),
      address: new FormControl(''),
      city: new FormControl(''),
      province: new FormControl(''),
      country: new FormControl(''),
      postalCode: new FormControl(''),
      referralSource: new FormControl(''),
      consentsToResearch: new FormControl(''),
      consentSource: new FormControl(ConsentSource.Doctor),
      consentsToPrivacyForm: new FormControl(''),
      linkedReferralPatientId: new FormControl(''),
      phoneNumberAndCountryCode: PatientFormGroup.getPhoneNumberAndCountryCodeFormGroup(),
      externalPatientId: new FormControl(''),
      emergencyContactName: new FormControl(''),
      emergencyContactEmail: new FormControl('', emailValidator),
      emergencyContactPhoneNumberAndCountryCode: PatientFormGroup.getPhoneNumberAndCountryCodeFormGroup(),
      pharmacyName: new FormControl(''),
      pharmacyPhoneNumberAndCountryCode: PatientFormGroup.getPhoneNumberAndCountryCodeFormGroup(),
      pharmacyFaxNumberAndCountryCode: PatientFormGroup.getPhoneNumberAndCountryCodeFormGroup()
    });
    this.controls.id.member = PatientFormControlType.Id;
    this.controls.patientDoctorId.member = PatientFormControlType.PatientDoctorId;
    this.controls.entryMethod.member = PatientFormControlType.EntryMethod;
    this.controls.firstName.member = PatientFormControlType.FirstName;
    this.controls.lastName.member = PatientFormControlType.LastName;
    this.controls.dateOfBirth.member = PatientFormControlType.DateOfBirth;
    this.controls.healthCard.member = PatientFormControlType.HealthCard;
    this.controls.gender.member = PatientFormControlType.Gender;
    this.controls.genderOther.member = PatientFormControlType.GenderOther;
    this.controls.phone.member = PatientFormControlType.Phone;
    this.controls.email.member = PatientFormControlType.Email;
    this.controls.address.member = PatientFormControlType.Address;
    this.controls.referralSource.member = PatientFormControlType.ReferralSource;
    this.controls.consentsToResearch.member = PatientFormControlType.ConsentsToResearch;
    this.controls.consentSource.member = PatientFormControlType.ConsentSource;
    this.controls.consentsToPrivacyForm.member = PatientFormControlType.consentsToPrivacyForm;
    this.controls.linkedReferralPatientId.member = PatientFormControlType.LinkedReferralPatientId;
    this.controls.emergencyContactName.member = PatientFormControlType.EmergencyContactName;
    this.controls.emergencyContactEmail.member = PatientFormControlType.EmergencyContactEmail;
    this.controls.externalPatientId.member = PatientFormControlType.ExternalPatientId;
    this.controls.phoneNumberAndCountryCode.member =
      PatientFormControlType.PhoneNumberAndCountryCode;
    this.controls.emergencyContactPhoneNumberAndCountryCode.member =
      PatientFormControlType.EmergencyContactPhoneNumberAndCountryCode;
    this.controls.pharmacyName.member = PatientFormControlType.pharmacyName;
    this.controls.pharmacyPhoneNumberAndCountryCode.member =
      PatientFormControlType.pharmacyPhoneNumberAndCountryCode;
    this.controls.pharmacyFaxNumberAndCountryCode.member =
      PatientFormControlType.pharmacyFaxNumberAndCountryCode;

    this.resetOnEntryMethodChange();

    PatientFormGroup.syncPhoneNumberWithPhoneNumberAndCountryCodeFormGroup(
      this.controls.phoneNumberAndCountryCode,
      this.controls.phone
    );
  }

  toApiInput(): CreatePatientInput | UpdatePatientInput {
    this.enable();
    const input = mergeWith({}, this.value, (value: any, srcValue: any, key: string) => {
      // AWS does not like empty strings for AWSEmail and Enums and nested object properties
      // so set them to null instead. And dateOfBirth is an AWSDate object so convert to the
      // format it expects.
      // If we run into this case a lot might be worth refactoring into some kind of
      // central helper service or FormGroup class that we extend.
      if (typeof srcValue === 'string' && srcValue.trim() === '') {
        return null;
      } else if (isPlainObject(srcValue) && isEmpty(cleanDeep(srcValue))) {
        return null;
      } else if (key === 'dateOfBirth') {
        // null date will return Dec, 1969 so use undefined instead
        return this.toAWSDate(this.controls.dateOfBirth.value);
      } else {
        merge(value, srcValue);
      }
    });
    delete input.entryMethod;

    delete input.phoneNumberAndCountryCode;
    if (isNullOrUndefined(input.externalPatientId)) {
      delete input.externalPatientId;
    }

    if (!input.linkedReferralPatientId) {
      delete input.linkedReferralPatientId;
    }

    this.disable();
    return input;
  }

  private toAWSDate(value: Date | string): string {
    if (typeof value === 'string') {
      // Date-shaped strings (optionally with an ISO time/timezone suffix): verify the written
      // parts form a REAL calendar date before trusting them — raw CSV junk like '0000-00-00'
      // is date-shaped but AppSync rejects it as an AWSDate. The written parts win over any
      // timezone math, since a date of birth is a calendar date.
      const dateShapedParts = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})([TtZz\s]|$)/);
      if (dateShapedParts) {
        const year = Number(dateShapedParts[1]);
        const monthIndex = Number(dateShapedParts[2]) - 1;
        const dayOfMonth = Number(dateShapedParts[3]);
        return isRealCalendarDate(year, monthIndex, dayOfMonth)
          ? formatDateAsAWSDateString(new Date(year, monthIndex, dayOfMonth))
          : null;
      }

      // Fallback: parse as date (may encounter timezone issue)
      const parsed = new Date(value);
      if (parsed.toString() === 'Invalid Date') {
        return null;
      }
      return formatDateAsAWSDateString(parsed);
    }
    if (value && value.toString() !== 'Invalid Date') {
      return formatDateAsAWSDateString(value);
    }
    return null;
  }

  public reset(
    value?: any,
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
    }
  ) {
    if (!value) {
      value = {};
    }
    if (!value.entryMethod) {
      value.entryMethod = value.id
        ? PatientEntryMethod.LookupExisting
        : PatientEntryMethod.AddNewPatient;
    }

    if (!value.consentSource) {
      value.consentSource = ConsentSource.Doctor;
    }

    super.reset(value, options);
  }

  public getControl(type: PatientFormControlType): AbstractControl | undefined {
    return this.controls[
      Object.keys(this.controls).find(controlKey => this.controls[controlKey].member === type)
    ];
  }

  private resetOnEntryMethodChange() {
    this.controls.entryMethod.valueChanges.subscribe(value => {
      const oldValue = this.value.entryMethod;
      if (
        oldValue === PatientEntryMethod.LookupExisting &&
        value === PatientEntryMethod.AddNewPatient
      ) {
        setTimeout(() => this.reset());
      }
    });
  }

  patchValue(patient: Partial<Patient>) {
    const valuesToPatch = {
      ...this.value,
      ...patient,
      emergencyContactPhoneNumberAndCountryCode:
        patient.emergencyContactPhoneNumberAndCountryCode || {},
      pharmacyPhoneNumberAndCountryCode: patient.pharmacyPhoneNumberAndCountryCode || {},
      pharmacyFaxNumberAndCountryCode: patient.pharmacyFaxNumberAndCountryCode || {}
    };

    if (patient.phone) {
      valuesToPatch[
        'phoneNumberAndCountryCode'
      ] = PatientFormGroup.getCountryCodeAndPhoneNumberFromPhoneNumber(patient.phone);
    }

    super.patchValue(valuesToPatch);
  }

  static getCountryCodeAndPhoneNumberFromPhoneNumber(phone) {
    if (!phone) return {};
    for (let i = 0; i < countryCodes.length; i++) {
      const countryCode = countryCodes[i].code.replace('+', '');
      let phoneNumber = parsePhoneNumber(phone, {
        defaultCallingCode: countryCode,
        defaultCountry: 'US'
      });
      if (phoneNumber.isValid()) {
        return {
          countryCode: `+${phoneNumber.countryCallingCode}`,
          phone: phoneNumber.nationalNumber
        };
      }
    }
  }
}
